const sectionsToRender = [
  {
    id: '#cart-counter',
    section: 'cart-counter',
    selector: '#shopify-section-cart-counter'
  },
  {
    id: '#CartDrawer-Body',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Body'
  }
];

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      event => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.setCartLink();
    this.parentElement.addEventListener(
      'shopify:section:select',
      () => this.open()
    );
    this.parentElement.addEventListener(
      'shopify:section:deselect',
      () => this.close()
    );
  }

  setCartLink() {
    const cartLink = document.querySelector('[data-cart-link]');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', event => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', event => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(opener) {
    if (opener) this.setActiveElement(opener);
    this.classList.add('is-visible');
    this.addEventListener(
      'transitionend',
      () => {
        this.focusOnCartDrawer();
      },
      { once: true }
    );
    this.lockBodyScroll();
  }

  close() {
    const wasVisible = this.classList.contains('is-visible');

    this.classList.remove('is-visible');
    removeTrapFocus(this.activeElement);

    // ALWAYS release the lock we took, before anything can return early.
    // bodyScrollLock is reference-based, so letting go of our own target
    // cannot release anyone else's - and the header menu does not use this
    // library at all, it sets body.style.overflow itself.
    //
    // This used to sit behind the menu-open check below. HeaderDrawer leaves
    // `menu-open` on the header whenever the menu is closed by
    // instantlyHideDrawer() (which fires on resize - and on iOS Safari, the
    // URL bar collapsing IS a resize). One stale flag and every later close()
    // returned before unlocking: on iOS the document touchmove blocker stayed
    // attached and the page could not be scrolled for the rest of the session.
    this.unlockBodyScroll(wasVisible);

    const isHeaderMenuOpen = !!header && header.classList.contains('menu-open');

    if (isHeaderMenuOpen) {
      return;
    }

    // if we are on the cart page, resubmit form
    if (window.location.pathname === '/cart') {
      const cartDrawerForm = document.getElementById('CartDrawer-FormSummary');
      if (cartDrawerForm) {
        cartDrawerForm.submit();
      }
    }

  }

  // bodyScrollLock matches locks by object identity, so the node we release has
  // to be the very node we locked. Re-querying [role="dialog"] at close time is
  // not safe: a cart repaint can replace that element while the drawer is open,
  // and handing the library a node it has never seen removes nothing. The body
  // position gets restored, but on iOS the document touchmove blocker is only
  // detached once the lock list is empty - so the page silently stops
  // scrolling. That was the "remove an item, close the drawer, page is stuck"
  // bug on mobile.
  lockBodyScroll() {
    const target = this.querySelector('[role="dialog"]');

    if (!target) return;

    this.scrollLockTargets = this.scrollLockTargets || new Set();
    this.scrollLockTargets.add(target);

    bodyScroll.lock(target);
  }

  unlockBodyScroll(wasVisible) {
    const targets = this.scrollLockTargets;

    this.scrollLockTargets = null;

    if (targets && targets.size) {
      targets.forEach(target => bodyScroll.unlock(target));
      return;
    }

    // Open, but we hold no record of the lock - markup swapped by an app, or
    // opened before this ran. A page left frozen is worse than dropping locks
    // we did not take.
    if (wasVisible) bodyScroll.clear();
  }

  // Call this after ANY repaint of the drawer.
  //
  // bodyScrollLock attaches its iOS touch handlers (ontouchstart/ontouchmove)
  // to the panel element itself, and decides whether a swipe is allowed from
  // that panel's live scrollTop/scrollHeight/clientHeight. A repaint that
  // swaps the panel, or that shrinks the content under a scrolled panel,
  // leaves the drawer unable to scroll on iPhone - Android never notices,
  // because off-iOS the library uses body{overflow:hidden} and attaches no
  // touch handlers at all.
  //
  // renderContents() gets this for free via open(). The gift repaint in
  // gift-card.js and repaintCartDrawer() in global.js do not, which is why
  // they call this directly.
  syncBodyScroll() {
    if (!this.classList.contains('is-visible')) return;

    const target = this.querySelector('[role="dialog"]');

    if (!target) return;

    // Content can be shorter than it was before the repaint; a scrollTop left
    // past the new end shows blank space the shopper cannot scroll back from.
    const maxScrollTop = Math.max(0, target.scrollHeight - target.clientHeight);

    if (target.scrollTop > maxScrollTop) target.scrollTop = maxScrollTop;

    const tracked = this.scrollLockTargets;
    const stale = tracked
      ? Array.from(tracked).filter(node => node !== target)
      : [];

    // Healthy case: the live panel is the one we locked. Do nothing at all.
    if (tracked && tracked.has(target) && stale.length === 0) return;

    // Drop panels that no longer exist, then lock the live one. Order matters:
    // unlocking restores the body position regardless of how many locks remain,
    // so the re-lock has to come last to put it back.
    stale.forEach(node => {
      bodyScroll.unlock(node);
      tracked.delete(node);
    });

    this.lockBodyScroll();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  focusOnCartDrawer() {
    const containerToTrapFocusOn = this.firstElementChild;
    const focusElement = this.querySelector('[data-drawer-close]');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  renderContents(response, open = true) {
    this.getSectionsToRender()?.forEach(section => {
      const sectionElement = document.querySelector(section.id);
      const sectionHTML = response?.sections?.[section.section];

      // Bail out on this section instead of throwing: a missing section must
      // never turn a successful add-to-cart into a visible error message.
      if (!sectionElement || !sectionHTML) return;

      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);

      if (innerHTML === null) return;

      sectionElement.innerHTML = innerHTML;
    });

    // Side effect of the cart having changed. It must not be able to reject
    // the add-to-cart promise of whoever called us.
    //
    // updateFreeShipping() publishes cartUpdate itself, off the same /cart.js
    // it already has to fetch. Publishing again here doubled every subscriber
    // (and so doubled the round trips) for no extra information.
    try {
      updateFreeShipping();
    } catch (error) {
      console.error('cart-drawer: cart update listeners failed', error);
    }

    if (!open) {
      return;
    }

    this.open();
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return parsed ? parsed.innerHTML : null;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return sectionsToRender;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
